/**
 * Archivio delle riunioni: le videochiamate fatte, con il verbale scritto
 * dall'AI dalla registrazione (riassunto, decisioni, cose da fare).
 * Il verbale arriva già per email a tutti; qui resta consultabile nel tempo,
 * senza doverlo ripescare dalla posta.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format, isFuture, formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';
import { toast } from 'sonner';
import {
  Video, FileText, Users, ListChecks, Copy, Loader2, ChevronDown,
  Clock, ArrowRight, Gavel, Hourglass,
} from 'lucide-react';
import { InboxSidebar } from '@/components/layout/InboxSidebar';
import { Button } from '@/components/ui/button';
import { videoApi, type VideoRoomItem } from '@/lib/api/endpoints';
import { cn } from '@/lib/utils';

export function MeetingsPage() {
  const roomsQuery = useQuery({
    queryKey: ['video-rooms'],
    queryFn: () => videoApi.list(),
    refetchInterval: 60_000,
  });
  const rooms = roomsQuery.data?.rooms ?? [];

  const upcoming = rooms.filter((r) => !r.ended_at && (!r.starts_at || isFuture(new Date(r.starts_at))));
  const past = rooms.filter((r) => !upcoming.includes(r));

  return (
    <div className="h-screen flex bg-background">
      <InboxSidebar />
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-8 py-8 space-y-8">

          <header className="flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0">
              <Video className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold leading-tight">Riunioni</h1>
              <p className="text-sm text-muted-foreground mt-1 max-w-prose">
                Ogni riunione registrata lascia il suo verbale: di cosa avete parlato, cosa avete deciso,
                chi deve fare cosa. Scritto dall'AI, inviato a tutti i partecipanti, archiviato qui.
              </p>
            </div>
          </header>

          {roomsQuery.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-10">
              <Loader2 className="h-4 w-4 animate-spin" /> Carico le riunioni…
            </div>
          ) : rooms.length === 0 ? (
            <div className="rounded-xl border border-dashed py-14 text-center">
              <Video className="h-7 w-7 mx-auto text-muted-foreground/50" />
              <p className="mt-3 text-sm font-medium">Nessuna videochiamata, per ora</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Creane una dal calendario: gli invitati ricevono il link e l'appuntamento.
              </p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => (window.location.href = '/calendar')}>
                Vai al calendario <ArrowRight className="h-3.5 w-3.5 ml-1.5" />
              </Button>
            </div>
          ) : (
            <>
              {upcoming.length > 0 && (
                <section className="space-y-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    In programma
                  </h2>
                  {upcoming.map((r) => <UpcomingRow key={r.id} room={r} />)}
                </section>
              )}

              {past.length > 0 && (
                <section className="space-y-2">
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Già fatte
                  </h2>
                  {past.map((r) => <PastRow key={r.id} room={r} />)}
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function whenLabel(r: VideoRoomItem): string {
  const d = r.starts_at ? new Date(r.starts_at) : null;
  if (!d) return 'senza orario';
  return format(d, "EEEE d MMMM 'alle' HH:mm", { locale: it });
}

function UpcomingRow({ room }: { room: VideoRoomItem }) {
  return (
    <div className="rounded-xl border p-4 flex items-center gap-4">
      <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
        <Clock className="h-4.5 w-4.5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-medium truncate">{room.title}</div>
        <div className="text-sm text-muted-foreground truncate">
          {whenLabel(room)}
          {room.guest_email ? ` · ${room.guest_email}` : ''}
        </div>
      </div>
      <Button variant="ghost" size="sm" onClick={() => {
        void navigator.clipboard.writeText(room.url);
        toast.success('Link copiato');
      }}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" onClick={() => window.open(room.url, '_blank', 'noopener')}>
        Entra
      </Button>
    </div>
  );
}

function PastRow({ room }: { room: VideoRoomItem }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn('rounded-xl border transition-colors', open && 'bg-muted/30')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left p-4 flex items-center gap-4"
      >
        <div className={cn(
          'h-9 w-9 rounded-lg flex items-center justify-center shrink-0',
          room.has_minutes ? 'bg-indigo-50 text-indigo-600'
            : room.minutes_pending ? 'bg-amber-50 text-amber-600'
            : 'bg-muted text-muted-foreground'
        )}>
          {room.minutes_pending ? <Hourglass className="h-4.5 w-4.5" /> : <FileText className="h-4.5 w-4.5" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{room.title}</div>
          <div className="text-sm text-muted-foreground truncate">
            {room.starts_at
              ? formatDistanceToNow(new Date(room.starts_at), { addSuffix: true, locale: it })
              : 'data non indicata'}
            {room.has_minutes ? ' · verbale pronto'
              : room.minutes_pending ? ' · verbale in preparazione'
              : ' · nessuna registrazione'}
          </div>
        </div>
        {room.has_minutes && (
          <ChevronDown className={cn('h-4 w-4 text-muted-foreground transition-transform shrink-0', open && 'rotate-180')} />
        )}
      </button>

      {open && (room.has_minutes
        ? <MinutesPanel roomId={room.id} />
        : (
          <p className="px-4 pb-4 text-sm text-muted-foreground">
            {room.minutes_pending
              ? 'La registrazione è arrivata, il verbale è in lavorazione. Torna fra un minuto.'
              : 'Questa riunione non è stata registrata, quindi non c\'è un verbale. Per averlo, avvia la registrazione dal menu ⋮ durante la chiamata.'}
          </p>
        )
      )}
    </div>
  );
}

function MinutesPanel({ roomId }: { roomId: string }) {
  const [showTranscript, setShowTranscript] = useState(false);
  const q = useQuery({
    queryKey: ['video-room', roomId, showTranscript],
    queryFn: () => videoApi.detail(roomId, showTranscript),
  });

  if (q.isLoading) {
    return (
      <div className="px-4 pb-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Apro il verbale…
      </div>
    );
  }
  if (!q.data) return <p className="px-4 pb-4 text-sm text-muted-foreground">Verbale non disponibile.</p>;

  const { room, participants } = q.data;
  const decisions = room.decisions ?? [];
  const actions = room.action_items ?? [];

  return (
    <div className="px-4 pb-5 space-y-5 border-t pt-4">
      <p className="text-sm leading-relaxed max-w-prose">{room.summary}</p>

      {decisions.length > 0 && (
        <section>
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            <Gavel className="h-3.5 w-3.5" /> Decisioni
          </h3>
          <ul className="space-y-1.5">
            {decisions.map((d, i) => (
              <li key={i} className="text-sm flex gap-2">
                <span className="text-muted-foreground shrink-0">{i + 1}.</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {actions.length > 0 && (
        <section>
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            <ListChecks className="h-3.5 w-3.5" /> Cose da fare
          </h3>
          <ul className="space-y-1.5">
            {actions.map((a, i) => (
              <li key={i} className="text-sm flex gap-2">
                <span className="text-indigo-500 shrink-0">•</span>
                <span>
                  {a.what}
                  {a.who && <span className="font-medium text-indigo-700"> — {a.who}</span>}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {room.next_steps && (
        <p className="text-sm">
          <span className="font-semibold">Prossimo passo:</span> {room.next_steps}
        </p>
      )}

      {participants.length > 0 && (
        <section>
          <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
            <Users className="h-3.5 w-3.5" /> Chi c'era
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {participants.map((p, i) => (
              <span key={i} className="text-xs rounded-full bg-muted px-2.5 py-1">
                {p.name || p.email}
              </span>
            ))}
          </div>
        </section>
      )}

      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-muted-foreground">
          {room.minutes_sent_at
            ? `Inviato per email ${formatDistanceToNow(new Date(room.minutes_sent_at), { addSuffix: true, locale: it })}`
            : 'Non ancora inviato per email'}
        </span>
        {room.has_transcript && (
          <Button variant="ghost" size="sm" onClick={() => setShowTranscript((v) => !v)}>
            {showTranscript ? 'Nascondi trascrizione' : 'Leggi la trascrizione'}
          </Button>
        )}
      </div>

      {showTranscript && room.transcript && (
        <div className="rounded-lg bg-muted/60 p-3 max-h-72 overflow-y-auto">
          <p className="text-xs leading-relaxed whitespace-pre-wrap text-muted-foreground">{room.transcript}</p>
        </div>
      )}
    </div>
  );
}
